import type { IntlShape } from '@formatjs/intl'
import { isPlanLimitError } from './planLimitError'
import { showErrorToast } from './toast'

/**
 * t() signature mirrored from useLanguage().
 */
type TFn = (key: string, params?: Parameters<IntlShape['formatMessage']>[1]) => string

type FormatParams = Parameters<IntlShape['formatMessage']>[1]

interface ApiErrorPayload {
  code?: string
  message?: string
  params?: FormatParams
  statusCode?: number
}

interface TranslatedError {
  title: string
  message: string
}

/**
 * Map an API error into a user-facing title + message.
 *
 * The API responds with `{ code, message, params, statusCode }`. We map
 * `code` to a translation key under `errors.<code>` and pass `params` to
 * `t()` for templated copy. The English `message` from the envelope is the
 * fallback when no translation exists yet — it's also what surfaces in
 * Sentry / dev tools.
 *
 * A request error without a `code` (validation refusal, server error, too
 * many attempts, no network) gets a translated sentence: its own `message`
 * is axios' technical one ("Request failed with status code 400").
 */
export function translateApiError(t: TFn, error: unknown): TranslatedError {
  const axiosLike = error as
    | { response?: { status?: number; data?: ApiErrorPayload }; isAxiosError?: boolean; message?: string }
    | undefined
  const data = axiosLike?.response?.data ?? undefined
  const title = t('errors.title')

  if (data?.code) {
    const key = `errors.${data.code}`
    const translated = t(key, data.params)
    // @formatjs's formatMessage falls back to the message id when the key
    // is missing. Detect that and use the API's English fallback instead.
    if (translated && translated !== key) {
      return { title, message: translated }
    }
    if (typeof data.message === 'string' && data.message) return { title, message: data.message }
  }

  // An HTTP error without a usable code (e.g. a validation 400): never show
  // axios' raw "Request failed with status code 400" to the person.
  if (axiosLike?.response) {
    // Too many attempts: say to wait, « réessayez » would be refused again.
    if (axiosLike.response.status === 429) return { title, message: t('errors.tooManyRequests') }
    return { title, message: t('errors.generic') }
  }
  // No response at all: the request never reached the API.
  if (axiosLike?.isAxiosError) return { title, message: t('api.NetworkError') }

  const fallback = axiosLike?.message ?? t('errors.generic')
  return { title, message: fallback }
}

/**
 * True for a 400 that carries no `code`: the API's field validation refused
 * the request. Screens that send a single checked field use it to name that
 * field instead of showing the generic sentence.
 */
export function isUncodedBadRequest(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: ApiErrorPayload } } | undefined)
    ?.response
  return response?.status === 400 && !response.data?.code
}

/**
 * True when the API refused an AI request because the account used up its
 * daily allowance (HTTP 429, renewed by itself over 24 hours). Screens that
 * otherwise say « Réessayez » use it to show the limit sentence instead:
 * retrying would be refused again.
 */
export function isAiDailyLimitError(error: unknown): boolean {
  const data = (error as { response?: { data?: ApiErrorPayload } } | undefined)?.response?.data
  return data?.code === 'AI_DAILY_LIMIT_REACHED'
}

/**
 * Convenience: translate then show as an error toast.
 *
 * Plan-limit refusals (HTTP 402 + `PLAN_*` code) are skipped: the axios
 * interceptor already raises the Premium paywall for them, and a toast on top
 * would repeat the same message.
 */
export function showApiErrorToast(t: TFn, error: unknown): void {
  if (isPlanLimitError(error)) {
    return
  }
  const { title, message } = translateApiError(t, error)
  showErrorToast(title, message)
}

/**
 * True when the request never reached the API (no `response` at all) — a
 * dropped connection, not a 4xx/5xx the server actually answered. Callers
 * that offer an offline fallback (the entrance scan) use this to tell "the
 * network is down" apart from a real API error worth a toast.
 */
export function isNetworkError(error: unknown): boolean {
  const axiosLike = error as { response?: unknown; isAxiosError?: boolean } | undefined
  return !!axiosLike?.isAxiosError && !axiosLike?.response
}
